import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { env } from "../config/env";
import { refreshAccessToken, tokens } from "../api/client";
import { network, notifications } from "../platform";
import { useAuth } from "../auth/AuthProvider";
import { RealtimeClient, RealtimeStatus } from "./RealtimeClient";

type Ctx = { status: RealtimeStatus; lastSync: Date | null; reconnect: () => void };
const RealtimeContext = createContext<Ctx>({ status: "idle", lastSync: null, reconnect: () => {} });

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [status, setStatus] = useState<RealtimeStatus>("idle");
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const clientRef = useRef<RealtimeClient | null>(null);

  useEffect(() => {
    if (!user) return undefined;
    const client = new RealtimeClient({
      url: () => (tokens.get() ? `${env.wsBase}?token=${encodeURIComponent(tokens.get() as string)}` : null),
      onStatus: setStatus,
      onAuthError: async () => { await refreshAccessToken(); },
      onMessage: (msg) => {
        if (msg.type === "hello") {
          qc.invalidateQueries();
          setLastSync(new Date());
        } else if (msg.type === "booking.updated") {
          ["bookings", "booking", "dispatch", "responder"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
          setLastSync(new Date());
        } else if (msg.type === "responder.updated") {
          ["dispatch", "responder"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
        } else if (msg.type === "notification") {
          qc.invalidateQueries({ queryKey: ["notifications"] });
          const n = msg.notification;
          toast(n.title, { description: n.body });
          if (document.hidden) notifications.show(n.title, n.body);
        }
      },
    });
    clientRef.current = client;
    client.start();
    const unsub = network.subscribe((online) => { if (online) client.reconnectNow(); else setStatus("closed"); });
    const onVisible = () => {
      if (document.visibilityState === "visible") { client.reconnectNow(); qc.invalidateQueries(); }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      unsub();
      document.removeEventListener("visibilitychange", onVisible);
      client.stop();
    };
  }, [user?.id, qc]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo(() => ({ status, lastSync, reconnect: () => clientRef.current?.reconnectNow() }), [status, lastSync]);
  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export const useRealtime = () => useContext(RealtimeContext);
